/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import type { TrackFrameUnit } from '@com/components/timeline-panel/types'
import {
  HistoryStateRecorder,
  RecorderOptions,
} from '@com/utils/history-recorder'
import store from '@ps/store'
import { returnAndSavedAsPrevious } from '@ps/store/interactive-video/brain'
import type { IvBrain } from '@ps/store/interactive-video/brain/types'
import {
  getTimelineBySceneUuid,
  setTimeline,
} from '@ps/store/interactive-video/timeline/timeline'
import { IvSegment, IvTrack } from '@ps/store/interactive-video/timeline/types'
import { cloneDeep, fromPairs } from 'lodash-es'
import { EventBus, Event } from '@ps/utils/event-bus'

export type HistoryState = {
  scenes: Record<IvSegment['sceneUuid'], IvTrack[]>
  brain: IvBrain

  currentFrame: TrackFrameUnit
  currentBrainBranch: IvSegment['sceneUuid'][]
  currentSceneUuid: IvSegment['sceneUuid']
}

const prefilter: RecorderOptions<any>['prefilter'] = (path, key) => {
  // returns true will be filtered. eg: ['cuttingDuration', 'before', 'behind'].includes(key)
  return false
}

let _inited = false
let recorder: HistoryStateRecorder<HistoryState> | null = null
const MESSAGE_MAIN = 'useIvHistory'

const UninitializedMessage = `[${MESSAGE_MAIN} error]: The created state recorder is not initialized, please create it before this.`

const log = (message: string, ...args: any[]) =>
  process.env.NODE_ENV === 'development' &&
  console.log(`[${MESSAGE_MAIN} message]: ${message}`, ...args)

export function initIvHistory(): void {
  if (_inited) {
    return
  }

  recorder = new HistoryStateRecorder<HistoryState>(
    () => {
      const brain: IvBrain = store.state.ivBrain.brainData || []

      return {
        scenes: fromPairs(
          brain.map((item) => [
            item.sceneUuid,
            getTimelineBySceneUuid(item.sceneUuid),
          ]),
        ),
        brain,

        currentFrame: store.state.ivTimeline.currentFrame,
        currentBrainBranch: [
          ...(store.getters['ivBrain/brainBranchUuids'] || []),
        ],
        currentSceneUuid:
          store.state.workspace.sceneManager.currentSceneNode?.uuid ?? '',
      }
    },
    {
      max: Infinity,
      prefilter,
    },
  )

  log(`%c State of history recorder initialized.`, 'color: #529b2e')
  _inited = true
}

export function useIvHistory() {
  return {
    push() {
      if (!_inited || !recorder) {
        throw new Error(UninitializedMessage)
      }

      const changed = recorder.push()

      log('push state', cloneDeep(recorder.savedState))
      store.dispatch('ivTimeline/refreshTimeline')

      return changed
    },
    undo() {
      if (!_inited || !recorder) {
        throw new Error(UninitializedMessage)
      }
      recorder.undo()
      log('undo state', cloneDeep(recorder.savedState))

      applyState(recorder.savedState)
    },
    redo() {
      if (!_inited || !recorder) {
        throw new Error(UninitializedMessage)
      }
      recorder.redo()

      applyState(recorder.savedState)
    },
  }
}

function applyState(state: HistoryState) {
  returnAndSavedAsPrevious(state.currentSceneUuid, state.currentBrainBranch)
  applyBrainState(state.brain)
  applySceneTracksState(state.scenes)

  store.commit('ivTimeline/updateCurrentFrame', state.currentFrame)
}

function applyBrainState(brain: HistoryState['brain']): void {
  store.commit('ivBrain/updateBrainData', cloneDeep(brain))
  EventBus.$emit(Event.IV_BRAIN_CHANGE)
}
function applySceneTracksState(scenes: HistoryState['scenes']): void {
  Object.entries(scenes).forEach(([sceneUuid, tracks]) => {
    setTimeline(sceneUuid, tracks)
  })

  store.dispatch('ivTimeline/refreshTimeline')
}
